// File: src/components/admin/plans/CreatePlanDialog.js
// AC19: create a brand-new plan with a unique id and minimal seed
// fields. Created plans default to archived (the editor must publish them).

import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, Stack, MenuItem, Alert, CircularProgress,
} from '@mui/material';
import { mainApi } from '../../../services/mainApi';

const ID_RE = /^[a-z][a-z0-9_-]{1,31}$/;

export default function CreatePlanDialog({ open, onClose, onCreated }) {
    const [id, setId] = useState('');
    const [name, setName] = useState('');
    const [price, setPrice] = useState(0);
    const [currency, setCurrency] = useState('EUR');
    const [billingInterval, setBillingInterval] = useState('month');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    function reset() {
        setId(''); setName(''); setPrice(0); setCurrency('EUR'); setBillingInterval('month');
        setError(null); setSaving(false);
    }

    async function handleCreate() {
        if (!ID_RE.test(id)) {
            setError('Plan id must start with a lowercase letter and contain only [a-z0-9_-] (max 32 chars).');
            return;
        }
        setSaving(true);
        setError(null);
        try {
            const plan = await mainApi.createPlan({
                id, name: name || id, price, currency, interval: billingInterval,
            });
            reset();
            onClose();
            if (onCreated) onCreated(plan);
        } catch (e) {
            setError(e?.response?.data?.error || e.message);
            setSaving(false);
        }
    }

    return (
        <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="sm" fullWidth
            data-testid="create-plan-dialog">
            <DialogTitle>New plan</DialogTitle>
            <DialogContent dividers>
                <Stack spacing={2} sx={{ pt: 1 }}>
                    {error && <Alert severity="error" data-testid="create-plan-error">{error}</Alert>}
                    <TextField
                        label="Plan id"
                        size="small"
                        value={id}
                        onChange={(e) => setId(e.target.value)}
                        helperText="Lowercase, [a-z0-9_-], starts with a letter. Used in URLs and Redis keys."
                        inputProps={{ 'data-testid': 'create-plan-id' }}
                    />
                    <TextField
                        label="Internal name"
                        size="small"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        inputProps={{ 'data-testid': 'create-plan-name' }}
                    />
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="Price (cents)"
                            type="number"
                            size="small"
                            value={price}
                            onChange={(e) => setPrice(Number(e.target.value))}
                            sx={{ width: 160 }}
                            inputProps={{ 'data-testid': 'create-plan-price', min: 0 }}
                        />
                        <TextField
                            select
                            label="Currency"
                            size="small"
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            sx={{ width: 120 }}
                            inputProps={{ 'data-testid': 'create-plan-currency' }}
                        >
                            <MenuItem value="EUR">EUR</MenuItem>
                            <MenuItem value="USD">USD</MenuItem>
                            <MenuItem value="GBP">GBP</MenuItem>
                        </TextField>
                        <TextField
                            select
                            label="Interval"
                            size="small"
                            value={billingInterval}
                            onChange={(e) => setBillingInterval(e.target.value)}
                            sx={{ width: 120 }}
                            inputProps={{ 'data-testid': 'create-plan-interval' }}
                        >
                            <MenuItem value="month">month</MenuItem>
                            <MenuItem value="year">year</MenuItem>
                        </TextField>
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={() => { reset(); onClose(); }} disabled={saving}
                    data-testid="create-plan-cancel">
                    Cancel
                </Button>
                <Button variant="contained" onClick={handleCreate} disabled={saving || !id}
                    data-testid="create-plan-submit">
                    {saving ? <CircularProgress size={20} /> : 'Create plan'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
